import { Link, router } from '@inertiajs/react'
import { Check, Funnel, MailPlus, RefreshCw, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useState } from 'react'
import { useOrgPath } from '@/lib/pathFor'
import { formatTime, extractSenderName } from '@/lib/formatters'

import type { ThreadItem } from '@/pages/Inbox/Index'

interface ThreadCounts {
  open: number
  pending: number
  closed: number
}

interface ThreadListProps {
  threads: ThreadItem[]
  filter: string
  attention?: string | null
  counts: ThreadCounts
  selectedThreadId?: string
  search: string
  next_cursor: string | null
  current_cursor: string | null
  onRefresh: () => void
  isRefreshing: boolean
}

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'pending', label: 'Pending' },
  { key: 'closed', label: 'Closed' },
] as const

const REVIEW_REASON_BADGES: Record<string, { label: string; className: string }> = {
  agent_failed: { label: 'Agent Failed', className: 'bg-amber-100 text-amber-800' },
  draft_awaiting_review: { label: 'Draft Awaiting Review', className: 'bg-blue-50 text-blue-700' },
  unassigned: { label: 'No shipment linked', className: 'bg-gray-100 text-gray-600' },
  send_failed: { label: 'Send Failed', className: 'bg-red-100 text-red-800' },
  ingestion_failed: { label: 'Ingestion Failed', className: 'bg-orange-100 text-orange-800' },
  escalated: { label: 'Escalated', className: 'bg-amber-100 text-amber-800' },
}

const ATTENTION_FILTERS = [
  { key: null, label: 'All threads' },
  { key: 'all', label: 'All attention' },
  { key: 'escalated', label: 'Escalated' },
  { key: 'send_failed', label: 'Send Failed' },
  { key: 'agent_failed', label: 'Agent Failed' },
  { key: 'ingestion_failed', label: 'Ingestion Failed' },
  { key: 'unknown_sender', label: 'Unknown Sender' },
  { key: 'unassigned', label: 'No shipment linked' },
  { key: 'draft_awaiting_review', label: 'Draft Awaiting Review' },
] as const

export default function ThreadList({ threads, filter, attention = null, counts, selectedThreadId, search: initialSearch, next_cursor, current_cursor, onRefresh, isRefreshing }: ThreadListProps) {
  const [search, setSearch] = useState(initialSearch)
  const pathFor = useOrgPath()
  const activeAttentionLabel = ATTENTION_FILTERS.find((option) => option.key === attention)?.label

  function buildInboxUrl(path: string, opts: { filter?: string; attention?: string | null; search?: string; cursor?: string | null } = {}) {
    const urlParams = new URLSearchParams()
    urlParams.set('filter', opts.filter ?? filter)
    const nextAttention = opts.attention === undefined ? attention : opts.attention
    if (nextAttention) urlParams.set('attention', nextAttention)
    if (opts.search ?? search) urlParams.set('search', (opts.search ?? search)!)
    if (opts.cursor) urlParams.set('cursor', opts.cursor)
    return pathFor(`${path}?${urlParams.toString()}`)
  }

  function switchFilter(f: string) {
    router.visit(buildInboxUrl('/inbox', { filter: f }), { preserveState: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.visit(buildInboxUrl('/inbox'), { preserveState: true })
  }

  function switchAttention(nextAttention: string | null) {
    router.visit(buildInboxUrl('/inbox', { attention: nextAttention }), { preserveState: true })
  }

  function selectThread(id: string) {
    router.visit(buildInboxUrl(`/inbox/threads/${id}`, { cursor: current_cursor }), { preserveState: true })
  }

  return (
    <div className="flex h-full w-[340px] flex-col border-r bg-background">
      {/* Filter Tabs */}
      <div className="flex items-end h-11 px-3 border-b">
        <div className="flex h-full items-end flex-1">
          {FILTERS.map(({ key, label }) => {
            const isActive = filter === key
            const count = counts[key as keyof ThreadCounts]
            return (
              <button
                key={key}
                onClick={() => switchFilter(key)}
                className={`flex items-center gap-1 h-8 px-2 text-[13px] cursor-pointer ${
                  isActive
                    ? 'font-semibold text-primary border-b-2 border-primary'
                    : 'font-medium text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
                {count > 0 && (
                  <span className={`flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white bg-gray-400`}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                href={pathFor('/inbox/compose')}
                className="mb-1.5 mr-1 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                aria-label="Compose"
              >
                <MailPlus className="h-4 w-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Compose</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="mb-1.5 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-60 cursor-pointer"
                aria-label="Refresh inbox"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Refresh inbox</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex items-center gap-2 h-11 px-3 border-b">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search threads..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 border-0 bg-transparent px-0 text-[13px] shadow-none focus-visible:ring-0"
        />
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Filter by attention status"
              title={activeAttentionLabel || 'Filter by attention status'}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-muted cursor-pointer ${
                attention ? 'text-primary bg-muted' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Funnel className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-48 gap-1 p-1">
            <div className="px-1.5 py-1 text-[10px] font-medium text-muted-foreground uppercase">Attention status</div>
            {ATTENTION_FILTERS.map((option) => {
              const isActive = option.key === attention
              return (
                <button
                  key={option.key || 'none'}
                  type="button"
                  onClick={() => switchAttention(option.key)}
                  className={`flex w-full items-center justify-between rounded px-1.5 py-1 text-left text-[11px] leading-4 cursor-pointer ${
                    isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <span>{option.label}</span>
                  {isActive && <Check className="h-3 w-3" />}
                </button>
              )
            })}
          </PopoverContent>
        </Popover>
      </form>

      {/* List Items - One row per thread */}
      <div className="flex-1 overflow-y-auto">
        {threads.map((thread) => {
          const isSelected = thread.id === selectedThreadId
          const hasMultipleEmails = thread.emails_count > 1
          const latestReviewReason = thread.review_reasons.find((reason) => REVIEW_REASON_BADGES[reason])

          return (
            <button
              key={thread.id}
              onClick={() => selectThread(thread.id)}
              className={`flex w-full flex-col gap-1.5 px-4 py-3 text-left border-b cursor-pointer ${
                isSelected
                  ? 'bg-blue-50 border-border'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              {/* Header: Subject + Date + Email Count */}
              <div className="flex items-center justify-between w-full">
                <span className="text-[13px] font-semibold text-foreground truncate">
                  {thread.subject || '(no subject)'}
                </span>
                <div className="flex items-center gap-1.5 ml-2 shrink-0">
                  {hasMultipleEmails && (
                    <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      {thread.emails_count}
                    </span>
                  )}
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                    {thread.latest_date ? formatTime(thread.latest_date) : ''}
                  </span>
                </div>
              </div>

              {/* Participants */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground truncate w-full">
                <span>{thread.participants.slice(0, 3).map(extractSenderName).join(', ')}</span>
                {thread.participants.length > 3 && (
                  <span>+{thread.participants.length - 3} more</span>
                )}
              </div>

              {/* Badges Row */}
              <div className="flex flex-wrap gap-1.5">
                {latestReviewReason && (
                  <Badge variant="secondary" size="sm" className={`font-medium ${REVIEW_REASON_BADGES[latestReviewReason].className}`}>
                    {REVIEW_REASON_BADGES[latestReviewReason].label}
                  </Badge>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t px-3 py-2">
        <button
          onClick={() => router.visit(buildInboxUrl('/inbox'), { preserveState: true })}
          disabled={!current_cursor}
          className={`text-xs font-medium ${current_cursor ? 'text-foreground hover:underline cursor-pointer' : 'text-muted-foreground/50 cursor-default'}`}
        >
          ‹ Previous
        </button>
        <button
          onClick={() => next_cursor && router.visit(buildInboxUrl('/inbox', { cursor: next_cursor }), { preserveState: true })}
          disabled={!next_cursor}
          className={`text-xs font-medium ${next_cursor ? 'text-foreground hover:underline cursor-pointer' : 'text-muted-foreground/50 cursor-default'}`}
        >
          Next ›
        </button>
      </div>
    </div>
  )
}
